import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { USER_DATA_TABLES } from "@comandero/powersync";

/**
 * Replaces VISITOR_ID with the newly authenticated user's ID
 * across all relevant tables and the offline CRUD queue.
 *
 * This ensures that:
 * 1. The local UI instantly reflects the new owner without flashing.
 * 2. The PowerSync upload queue sends the correct `user_id` to the backend.
 */
export async function sweepVisitorData(
  db: AbstractPowerSyncDatabase | null,
  newUserId: string,
  visitorId: string,
) {
  if (!db || !newUserId) {
    return;
  }

  try {
    // 1. Update the actual table rows so UI queries pick them up immediately
    await Promise.all(
      USER_DATA_TABLES.map(async (table) =>
        db.execute(`UPDATE ${table} SET user_id = ? WHERE user_id = ?`, [newUserId, visitorId]),
      ),
    );

    // 2. Rewrite the queued operations so the network payload has the correct
    // user_id. PowerSync's `ps_crud.data` is a JSON envelope
    // `{"op":…,"type":…,"id":…,"data":{<column>:<value>,…}}` — the row's ACTUAL
    // column values live NESTED under `$.data.*` (see `CrudEntry.fromRow`). The
    // previous top-level `$.user_id` matched NOTHING, so the queued op stayed
    // stamped with VISITOR_ID (CC-27). (comandero's guest data is user_id-keyed
    // at the sweep level; organizations are created server-side with fresh UUIDs
    // — NOT org_id == user_id like stockpilot's WMS — so there is no client-side
    // organization_id to rewrite here.)
    await db.execute(
      `
      UPDATE ps_crud
      SET data = json_set(data, '$.data.user_id', ?)
      WHERE json_extract(data, '$.data.user_id') = ?
    `,
      [newUserId, visitorId],
    );
  } catch (error) {
    console.error("[Sweep] Failed to sweep visitor data:", error);
  }
}
